import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight, ChevronDown, Bot, MessageCircle, Globe, BarChart3, UserCheck } from "lucide-react";
import Logo from "./Logo";
import { CASE_STUDIES } from "../data/caseStudies";
import { CASE_EXTRAS } from "../data/caseStudiesExtra";

const platformPath = (link) => link.to ?? `/platform/${link.slug}`;

const PLATFORM_LINKS = [
  {
    slug: "voice-agents",
    to: "/",
    icon: Bot,
    label: "Voice Agents",
    desc: "Human-like voice AI for sales, support and collections.",
  },
  {
    slug: "whatsapp-automation",
    icon: MessageCircle,
    label: "WhatsApp Automation",
    desc: "Outbound HSM templates + two-way chat flows.",
  },
  {
    slug: "chat-agents",
    icon: Globe,
    label: "Chat Agents",
    desc: "Embeddable chat + voice widget for your site.",
  },
  {
    slug: "analytics",
    icon: BarChart3,
    label: "Analytics",
    desc: "Live dispositions, sentiment and quality scoring.",
  },
  {
    slug: "human-handoff",
    icon: UserCheck,
    label: "Human Handoff",
    desc: "Escalate with whispered context — no cold transfers.",
  },
];

const NAV = [
  { label: "Platform", href: "/platform", dropdown: "platform" },
  // { label: "Industries", href: "/industries" },
  { label: "Case Studies", href: "/case-studies", dropdown: "case-studies" },
  { label: "Integrations", href: "/integrations" },
  { label: "Blog", href: "/blog" },
  // { label: "Pricing", href: "/pricing" },
  { label: "Book Demo", href: "/book-demo" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const dropTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    // passive: the handler only reads scrollY, never preventDefault. A
    // non-passive scroll listener forces the browser to wait on the main thread
    // before scrolling, which froze scroll for ~2-3s on desktop while the
    // below-fold hydrated.
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [pathname]);

  const showDrop = (id) => {
    if (dropTimer.current) clearTimeout(dropTimer.current);
    setActiveDropdown(id);
  };
  const hideDrop = () => {
    if (dropTimer.current) clearTimeout(dropTimer.current);
    dropTimer.current = setTimeout(() => setActiveDropdown(null), 140);
  };

  const isActive = (href) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <header
      data-testid="navbar"
      className={
        "fixed inset-x-0 top-0 z-50 transition-all duration-300 " +
        (scrolled
          ? "border-b border-white/[0.06] bg-[rgba(5,8,22,0.82)] shadow-[0_8px_30px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          : "bg-[rgba(5,8,22,0.40)] backdrop-blur-md")
      }
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-2.5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link">
          <Logo light />
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          {NAV.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => showDrop(item.dropdown)}
                onMouseLeave={hideDrop}
              >
                <Link
                  to={item.href}
                  data-testid={
                    item.dropdown === "platform"
                      ? "nav-platform"
                      : "nav-case-studies"
                  }
                  className={
                    "inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors " +
                    (activeDropdown === item.dropdown || isActive(item.href)
                      ? "text-[#B8A9F5]"
                      : "text-white/[0.88] hover:text-white")
                  }
                >
                  {item.label}
                  <ChevronDown
                    className={
                      "h-3.5 w-3.5 transition-transform " +
                      (activeDropdown === item.dropdown ? "rotate-180" : "")
                    }
                  />
                </Link>
                <AnimatePresence>
                  {activeDropdown === item.dropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.98 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      data-testid={
                        item.dropdown === "platform"
                          ? "nav-platform-dropdown"
                          : "nav-case-studies-dropdown"
                      }
                      className={
                        "absolute left-1/2 top-full mt-3 -translate-x-1/2 overflow-hidden rounded-3xl border border-white/[0.08] bg-[rgba(15,18,35,0.96)] p-3 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6),0_0_0_1px_rgba(167,139,250,0.12)] backdrop-blur-2xl " +
                        (item.dropdown === "platform" ? "w-[520px]" : "w-[700px]")
                      }
                    >
                      <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#7D68C2]/20 blur-3xl" />
                      <div className="relative">
                        {item.dropdown === "platform" ? (
                          <>
                            <div className="mb-2 px-3 pt-1 text-[10px] font-bold uppercase tracking-wider text-white/65">
                              Platform capabilities
                            </div>
                            <div className="space-y-1">
                              {PLATFORM_LINKS.map((link) => (
                                <Link
                                  key={link.slug}
                                  to={platformPath(link)}
                                  data-testid={`nav-platform-${link.slug}`}
                                  onClick={() => setActiveDropdown(null)}
                                  className="group flex items-start gap-3 rounded-2xl border border-transparent px-3 py-2.5 transition-all hover:border-[rgba(167,139,250,0.35)] hover:bg-[rgba(125,104,194,0.12)]"
                                >
                                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#A78BFA]/15 text-[#A78BFA] transition-all group-hover:scale-110 group-hover:bg-[#A78BFA]/25 group-hover:text-[#C9BEF5] group-hover:shadow-[0_8px_25px_-6px_rgba(167,139,250,0.55)]">
                                    <link.icon className="h-4 w-4" />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-white group-hover:text-[#C9BEF5]">
                                      {link.label}
                                    </div>
                                    <div className="mt-0.5 text-[11px] leading-tight text-white/[0.78]">
                                      {link.desc}
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                            <div className="mt-2 flex items-center justify-between border-t border-white/[0.06] px-3 pt-3">
                              <Link
                                to="/platform"
                                onClick={() => setActiveDropdown(null)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[#A78BFA] hover:text-white"
                              >
                                View platform overview
                                <ChevronRight className="h-3 w-3" />
                              </Link>
                              <span className="text-[10px] text-white/55">
                                Voice · WhatsApp · Chat
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="mb-2 px-3 pt-1 text-[10px] font-bold uppercase tracking-wider text-white/65">
                              Industry deployments
                            </div>
                            <div className="grid grid-cols-2 gap-1">
                              {CASE_STUDIES.map((c) => {
                                const tagline =
                                  (CASE_EXTRAS[c.slug] &&
                                    CASE_EXTRAS[c.slug].tagline) ||
                                  "";
                                return (
                                  <Link
                                    key={c.slug}
                                    to={`/case-studies/${c.slug}`}
                                    data-testid={`nav-case-${c.slug}`}
                                    onClick={() => setActiveDropdown(null)}
                                    className="group flex items-start gap-3 rounded-2xl border border-transparent px-3 py-2.5 transition-all hover:border-[rgba(167,139,250,0.35)] hover:bg-[rgba(125,104,194,0.12)]"
                                  >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#A78BFA]/15 text-[#A78BFA] transition-all group-hover:scale-110 group-hover:bg-[#A78BFA]/25 group-hover:text-[#C9BEF5] group-hover:shadow-[0_8px_25px_-6px_rgba(167,139,250,0.55)]">
                                      <c.icon className="h-4 w-4" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-semibold text-white group-hover:text-[#C9BEF5]">
                                        {c.label}
                                      </div>
                                      <div className="mt-0.5 truncate text-[11px] leading-tight text-white/[0.78]">
                                        {tagline}
                                      </div>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                            <div className="mt-2 flex items-center justify-between border-t border-white/[0.06] px-3 pt-3">
                              <Link
                                to="/case-studies"
                                onClick={() => setActiveDropdown(null)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[#A78BFA] hover:text-white"
                              >
                                View all case studies
                                <ChevronRight className="h-3 w-3" />
                              </Link>
                              <span className="text-[10px] text-white/55">
                                7 industries · live deployments
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                className={
                  "rounded-full px-4 py-2 text-sm font-medium transition-colors " +
                  (isActive(item.href)
                    ? "text-[#B8A9F5]"
                    : "text-white/[0.88] hover:text-white hover:bg-white/[0.04]")
                }
              >
                {item.label}
              </Link>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {/* <Link
            to="/login"
            data-testid="nav-login-btn"
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white/[0.88] transition hover:bg-white/[0.06] hover:text-white"
          >
            Login
          </Link> */}
          <Link
            to="/book-demo"
            data-testid="nav-book-demo-btn"
            className="group inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#7D68C2] to-[#9B8AE8] px-5 py-2 text-sm font-semibold text-white shadow-[0_15px_40px_-12px_rgba(125,104,194,0.6)] transition hover:shadow-[0_20px_60px_-15px_rgba(155,138,232,0.7)]"
          >
            Book a Demo
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          className="rounded-lg p-2.5 text-white hover:bg-white/[0.06] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22 }}
            className="border-t border-white/[0.06] bg-[rgba(5,8,22,0.96)] backdrop-blur-xl lg:hidden"
          >
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
              {NAV.map((item) =>
                item.dropdown ? (
                  <div key={item.label}>
                    <button
                      onClick={() =>
                        setMobileExpanded(
                          mobileExpanded === item.dropdown
                            ? null
                            : item.dropdown,
                        )
                      }
                      data-testid={
                        item.dropdown === "platform"
                          ? "nav-mobile-platform-toggle"
                          : "nav-mobile-case-toggle"
                      }
                      className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-sm font-medium text-white/[0.88] hover:bg-white/[0.04] hover:text-white"
                    >
                      {item.label}
                      <ChevronDown
                        className={
                          "h-4 w-4 transition-transform " +
                          (mobileExpanded === item.dropdown ? "rotate-180" : "")
                        }
                      />
                    </button>
                    <AnimatePresence>
                      {mobileExpanded === item.dropdown && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-1 space-y-1 pl-3">
                            {item.dropdown === "platform"
                              ? PLATFORM_LINKS.map((link) => (
                                  <Link
                                    key={link.slug}
                                    to={platformPath(link)}
                                    onClick={() => setOpen(false)}
                                    className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 hover:bg-[rgba(125,104,194,0.12)]"
                                  >
                                    <link.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#A78BFA]" />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-white">
                                        {link.label}
                                      </div>
                                      <div className="text-[11px] text-white/[0.78]">
                                        {link.desc}
                                      </div>
                                    </div>
                                  </Link>
                                ))
                              : CASE_STUDIES.map((c) => {
                                  const tagline =
                                    (CASE_EXTRAS[c.slug] &&
                                      CASE_EXTRAS[c.slug].tagline) ||
                                    "";
                                  return (
                                    <Link
                                      key={c.slug}
                                      to={`/case-studies/${c.slug}`}
                                      onClick={() => setOpen(false)}
                                      className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 hover:bg-[rgba(125,104,194,0.12)]"
                                    >
                                      <c.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#A78BFA]" />
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-white">
                                          {c.label}
                                        </div>
                                        <div className="truncate text-[11px] text-white/[0.78]">
                                          {tagline}
                                        </div>
                                      </div>
                                    </Link>
                                  );
                                })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-3 text-sm font-medium text-white/[0.88] hover:bg-white/[0.04] hover:text-white"
                  >
                    {item.label}
                  </Link>
                ),
              )}
              <div className="mt-2 flex gap-2">
                <Link
                  to="/login"
                  onClick={() => {
                    setOpen(false);
                    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
                  }}
                  className="flex-1 rounded-full border border-white/15 py-2.5 text-center text-sm font-semibold text-white"
                >
                  Login
                </Link>
                <Link
                  to="/book-demo"
                  onClick={() => setOpen(false)}
                  className="flex-1 rounded-full bg-gradient-to-br from-[#7D68C2] to-[#9B8AE8] py-2.5 text-center text-sm font-semibold text-white"
                >
                  Book a Demo
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
